import { BUTTON, RADIO, CHECKBOX, ARIA_DISABLED } from './core-utilities/aria'

export default class BaseComponent {
  constructor(rootElement, ...args) {
    this.root = rootElement
    this.isTouch = this._isTouchDevice()
    this._checkIsTouchDevice()
    this.init(...args)
  }

  init(/* ...args */) {
    // Subclasses can override this to do any additional setup work that
    // would be considered part of a "constructor". Any additional arguments
    // besides root will be passed in here.
  }

  destroy() {
    // Subclasses may implement this method to release any resources /
    // deregister any listeners they have attached. An example of this might be
    // deregistering a resize event from the window object.
  }

  /**
   * Wrapper method to add an event listener to the component's root element.
   * This is most useful when listening for custom events.
   *
   * Add event listener and access component instance in handler like this:
   *
   * const compElem = document.querySelector('dhl-example-component')
   * const comp = compElem.dhlExampleComponent
   * comp.listen('someCustomEvent', evt => {
   *   evt.detail.component.someMethod()
   *   // - or -
   *   evt.target.dhlExampleComponent.someMethod()
   * })
   *
   * // - or -
   *
   * const compElem = document.querySelector('dhl-example-component')
   * compElem.addEventListener('someCustomEvent', evt => {})
   *
   * @param {string} evtType
   * @param {!Function} handler
   */
  listen(evtType, handler) {
    if (this.root) {
      this.root.addEventListener(evtType, handler)
    }
  }

  /**
   * Wrapper method to remove an event listener to the component's root element.
   * This is most useful when unlistening for custom events.
   * @param {string} evtType
   * @param {!Function} handler
   */
  unlisten(evtType, handler) {
    if (this.root) {
      this.root.removeEventListener(evtType, handler)
    }
  }

  /**
   * Fires a cross-browser-compatible custom event from the component root of
   * the given type, with the given data.
   * @param {string} evtType
   * @param {!Object} evtData
   * @param {boolean=} shouldBubble
   */
  emit(evtType, evtData = {}, shouldBubble = false) {
    evtData.component = this

    let evt
    if (typeof CustomEvent === 'function') {
      evt = new CustomEvent(evtType, {
        detail: evtData,
        bubbles: shouldBubble
      })
    } else {
      evt = document.createEvent('CustomEvent')
      evt.initCustomEvent(evtType, shouldBubble, false, evtData)
    }
    if (this.root) {
      this.root.dispatchEvent(evt)
    }
  }

  /**
   * Determines if the given element is a native button element
   * or has a `role="button"` attribute.
   * @param {elem} Element - to be investigated
   * @return {Boolean}
   * @private
   */
  _isButton(elem = this.root) {
    return this._isNativeButton(elem) || elem.matches(`[role="${BUTTON}"]`)
  }

  /**
   * Determines if the given element is a native `<button>` element.
   * @param {elem} Element - to be investigated
   * @return {Boolean}
   * @private
   */
  _isNativeButton(elem = this.root) {
    return elem.tagName.toLowerCase() === 'button'
  }

  /**
   * Determines if the given element is a native radio element
   * or has a `role="radio"` attribute.
   * @param {elem} Element - to be investigated
   * @return {Boolean}
   * @private
   */
  _isRadio(elem = this.root) {
    return this._isNativeRadio(elem) || elem.matches(`[role="${RADIO}"]`)
  }

  /**
   * Determines if the given element is a native `<input type="radio">` element.
   * @param {elem} Element - to be investigated
   * @return {Boolean}
   * @private
   */
  _isNativeRadio(elem = this.root) {
    return (
      elem.tagName.toLowerCase() === 'input' &&
      elem.getAttribute('type') === 'radio'
    )
  }

  /**
   * Determines if the given element is a native checkbox element
   * or has a `role="checkbox"` attribute.
   * @param {elem} Element - to be investigated
   * @return {Boolean}
   * @private
   */
  _isCheckbox(elem = this.root) {
    return this._isNativeCheckbox(elem) || elem.matches(`[role="${CHECKBOX}"]`)
  }

  /**
   * Determines if the given element is a native `<input type="checkbox">` element.
   * @param {elem} Element - to be investigated
   * @return {Boolean}
   * @private
   */
  _isNativeCheckbox(elem = this.root) {
    return (
      elem.tagName.toLowerCase() === 'input' &&
      elem.getAttribute('type') === 'checkbox'
    )
  }

  /**
   * Determines if the given element is disabled.
   * @param {elem} Element - to be investigated
   * @return {Boolean}
   * @private
   */
  _isDisabled(elem = this.root) {
    return elem.disabled || elem.hasAttribute(ARIA_DISABLED)
  }

  /**
   * Create a touch event
   */
  _isTouchDevice() {
    try {
      document.createEvent('TouchEvent')
      return true
    } catch (e) {
      return false
    }
  }

  /**
   * check device ist a touch devices
   * add util class for touch devices
   */
  _checkIsTouchDevice() {
    if (this.isTouch) {
      if (!document.body.classList.contains('u-Touch')) {
        document.body.classList.add('u-Touch')
      }
    } else {
      document.body.classList.remove('u-Touch')
    }
  }
}
